import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import PublicSiteHeader from '../components/public/PublicSiteHeader';
import PublicReveal from '../components/public/PublicReveal';
import LucideSymbolIcon from '../components/icons/LucideSymbolIcon';
import PublicSiteFooter from '../components/public/PublicSiteFooter';
import ScrollToTopButton from '../components/ScrollToTopButton';
import { APP_DISPLAY_NAME } from '../config/brandingAssets';
import { usePublicBranding } from '../hooks/usePublicBranding';
import { fetchPublicLegalDocuments } from '../services/api';

export type PublicLegalView = 'cgu' | 'confidentialite';

interface PublicLegalPageProps {
  type: PublicLegalView | 'CGU' | 'CONFIDENTIALITE';
}

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const LEGAL_HERO_IMAGE = `${PUBLIC_URL}/images/hero-banner-4.jpg`;

const LEGAL_TABS: { id: PublicLegalView; path: string; label: string; short: string; icon: string }[] = [
  { id: 'cgu', path: '/legal/cgu', label: "Conditions d'utilisation", short: 'CGU', icon: 'article' },
  {
    id: 'confidentialite',
    path: '/legal/confidentialite',
    label: 'Politique de confidentialité',
    short: 'Confidentialité',
    icon: 'shield',
  },
];

type LegalBlock =
  | { kind: 'heading'; id: string; text: string; level: 2 | 3 }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[] };

function normalizeType(type: string): PublicLegalView {
  const t = type.toLowerCase();
  return t === 'confidentialite' ? 'confidentialite' : 'cgu';
}

function formatUpdatedAt(iso: string | null) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

function slugify(text: string, index: number) {
  const base = text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
  return base ? `${base}-${index}` : `section-${index}`;
}

function interpolateLegalContent(raw: string) {
  return raw
    .replaceAll('{appName}', APP_DISPLAY_NAME)
    .replaceAll('JCONSULT Academia', APP_DISPLAY_NAME);
}

function parseLegalBlocks(raw: string): LegalBlock[] {
  const trimmed = interpolateLegalContent(raw).trim();
  if (!trimmed) return [];

  const chunks = trimmed.split(/\n\n+/).map((block) => block.trim()).filter(Boolean);
  const blocks: LegalBlock[] = [];
  let headingIndex = 0;

  for (const chunk of chunks) {
    const lines = chunk.split('\n').map((line) => line.trim()).filter(Boolean);
    if (lines.length === 0) continue;

    const markdownHeading = lines[0].match(/^#{1,3}\s+(.+)$/);
    if (markdownHeading) {
      const level = (lines[0].match(/^#+/)?.[0].length ?? 2) <= 2 ? 2 : 3;
      blocks.push({
        kind: 'heading',
        id: slugify(markdownHeading[1], headingIndex++),
        text: markdownHeading[1],
        level,
      });
      if (lines.length > 1) {
        blocks.push({ kind: 'paragraph', text: lines.slice(1).join('\n') });
      }
      continue;
    }

    const numberedHeading = lines[0].match(/^(\d+[.)]\s+.+)$/);
    const isShortHeading =
      lines.length === 1 &&
      lines[0].length < 120 &&
      (lines[0] === lines[0].toUpperCase() || numberedHeading || lines[0].endsWith(':'));

    if (isShortHeading) {
      blocks.push({
        kind: 'heading',
        id: slugify(lines[0], headingIndex++),
        text: lines[0].replace(/:$/, ''),
        level: numberedHeading ? 2 : 3,
      });
      continue;
    }

    if (lines.every((line) => /^[-•*]\s+/.test(line))) {
      blocks.push({
        kind: 'list',
        items: lines.map((line) => line.replace(/^[-•*]\s+/, '')),
      });
      continue;
    }

    blocks.push({ kind: 'paragraph', text: chunk });
  }

  return blocks;
}

function LegalBody({ raw }: { raw: string }) {
  const blocks = useMemo(() => parseLegalBlocks(raw), [raw]);

  if (blocks.length === 0) {
    return (
      <div className="rounded-public-card border border-dashed border-outline-variant/30 bg-surface-container-low px-6 py-12 text-center">
        <LucideSymbolIcon name="description" tone="muted" size={32} className="mx-auto" />
        <p className="mt-3 text-sm font-medium text-on-surface-variant">Contenu à venir.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {blocks.map((block, index) => {
        if (block.kind === 'heading') {
          const Tag = block.level === 2 ? 'h2' : 'h3';
          return (
            <Tag
              key={`${block.id}-${index}`}
              id={block.id}
              className={[
                'scroll-mt-28 font-extrabold tracking-tight text-slate-900',
                block.level === 2
                  ? 'border-b border-outline-variant/15 pb-3 pt-2 text-xl sm:text-2xl'
                  : 'pt-4 text-lg text-primary',
              ].join(' ')}
            >
              {block.text}
            </Tag>
          );
        }

        if (block.kind === 'list') {
          return (
            <ul
              key={`list-${index}`}
              className="ml-1 space-y-2.5 border-l-2 border-primary/20 pl-5 text-[15px] leading-relaxed text-slate-700"
            >
              {block.items.map((item) => (
                <li key={item.slice(0, 40)} className="relative">
                  <span className="absolute -left-5 top-2.5 h-1.5 w-1.5 rounded-full bg-primary/70" aria-hidden />
                  {item}
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p key={`p-${index}`} className="text-[15px] leading-[1.85] text-slate-700">
            {block.text.split('\n').map((line, lineIdx, lines) => (
              <span key={lineIdx}>
                {line}
                {lineIdx < lines.length - 1 ? <br /> : null}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}

function LegalTableOfContents({ raw }: { raw: string }) {
  const headings = useMemo(
    () => parseLegalBlocks(raw).filter((b): b is Extract<LegalBlock, { kind: 'heading' }> => b.kind === 'heading'),
    [raw],
  );

  if (headings.length === 0) return null;

  return (
    <nav aria-label="Sommaire du document" className="rounded-public-card border border-outline-variant/15 bg-white p-4 shadow-sm">
      <p className="text-[11px] font-bold uppercase tracking-wider text-primary">Sommaire</p>
      <ol className="mt-3 space-y-1.5">
        {headings.map((heading) => (
          <li key={heading.id}>
            <a
              href={`#${heading.id}`}
              className={[
                'block rounded-public-btn px-2 py-1.5 text-sm transition hover:bg-primary/5 hover:text-primary',
                heading.level === 2 ? 'font-semibold text-slate-800' : 'pl-4 font-medium text-on-surface-variant',
              ].join(' ')}
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default function PublicLegalPage({ type: typeProp }: PublicLegalPageProps) {
  const type = normalizeType(typeProp);
  const [content, setContent] = useState('');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { supportEmail } = usePublicBranding();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const legal = await fetchPublicLegalDocuments();
        const block = type === 'cgu' ? legal.cgu : legal.confidentialite;
        if (!cancelled) {
          setContent(block?.content ?? '');
          setUpdatedAt(block?.updatedAt ?? null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Impossible de charger le document.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [type]);

  const activeTab = LEGAL_TABS.find((t) => t.id === type) ?? LEGAL_TABS[0];
  const otherTab = LEGAL_TABS.find((t) => t.id !== type)!;
  const meta = formatUpdatedAt(updatedAt);

  return (
    <div className="min-h-screen bg-surface-container-lowest text-on-surface">
      <PublicSiteHeader />

      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${LEGAL_HERO_IMAGE})` }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, rgba(135, 35, 32, 0.78) 0%, rgba(107, 27, 24, 0.52) 55%, rgba(107, 27, 24, 0.28) 100%)',
          }}
          aria-hidden
        />
        <div className="absolute inset-0 opacity-30" aria-hidden>
          <div className="absolute -right-16 top-0 h-72 w-72 rounded-full bg-primary-container blur-[100px]" />
          <div className="absolute bottom-0 left-0 h-56 w-56 rounded-full bg-tertiary-container blur-[80px]" />
        </div>

        <div className="relative z-10 mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:py-20">
          <PublicReveal animation="fade-in-down">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-on-primary-container/80" aria-label="Fil d'Ariane">
            <Link to="/" className="font-medium text-white/90 transition hover:text-white hover:underline">
              Accueil
            </Link>
            <span aria-hidden>/</span>
            <span>Documents légaux</span>
            <span aria-hidden>/</span>
            <span className="font-semibold text-white">{activeTab.short}</span>
          </nav>

          <div className="mt-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white/90 backdrop-blur-sm">
                <LucideSymbolIcon name={activeTab.icon} tone="inherit" size={16} />
                Document légal
              </div>
              <h1 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
                {activeTab.label}
              </h1>
              <p className="mt-4 text-base leading-relaxed text-on-primary-container sm:text-lg">
                Texte officiel publié par l&apos;administration de <strong className="text-white">{APP_DISPLAY_NAME}</strong>.
                Consultez aussi la{' '}
                <Link to={otherTab.path} className="font-semibold text-white underline decoration-white/40 underline-offset-4 hover:decoration-white">
                  {otherTab.label.toLowerCase()}
                </Link>
                .
              </p>
              {meta ? (
                <p className="mt-6 inline-flex items-center gap-2 rounded-public-card border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm">
                  <LucideSymbolIcon name="history" tone="inherit" size={18} />
                  Mise à jour : {meta}
                </p>
              ) : null}
            </div>
          </div>
          </PublicReveal>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        <PublicReveal animation="fade-in-up">
        <div
          className="mb-8 inline-flex w-full flex-col gap-1 rounded-public-card border border-outline-variant/15 bg-white p-1.5 shadow-sm sm:w-auto sm:flex-row"
          role="tablist"
        >
          {LEGAL_TABS.map((tab) => (
            <Link
              key={tab.id}
              to={tab.path}
              role="tab"
              aria-selected={tab.id === type}
              className={[
                'inline-flex items-center justify-center gap-2 rounded-public-btn px-5 py-3 text-sm font-bold transition',
                tab.id === type
                  ? 'bg-primary text-white shadow-md shadow-primary/20'
                  : 'text-on-surface-variant hover:bg-surface-container-low hover:text-primary',
              ].join(' ')}
            >
              <LucideSymbolIcon name={tab.icon} tone={tab.id === type ? 'inherit' : 'muted'} size={18} />
              {tab.label}
            </Link>
          ))}
        </div>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,17rem)_1fr] lg:gap-10">
          {!loading && !error && content.trim() ? (
            <aside className="hidden lg:block">
              <div className="sticky top-24 space-y-4">
                <LegalTableOfContents raw={content} />
                <div className="rounded-public-card border border-primary/15 bg-primary/[0.04] p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-primary">Lecture</p>
                  <p className="mt-2 text-sm leading-relaxed text-on-surface-variant">
                    Ce document encadre l&apos;usage de la plateforme. En cas de doute, contactez le support avant toute action sensible.
                  </p>
                </div>
              </div>
            </aside>
          ) : null}

          <article className="min-w-0 overflow-hidden rounded-public-card border border-outline-variant/12 bg-white shadow-xl shadow-slate-900/[0.04]">
            <div className="border-b border-outline-variant/10 bg-gradient-to-r from-surface-container-low to-white px-6 py-5 sm:px-8">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <LucideSymbolIcon name={activeTab.icon} tone="inherit" size={24} />
                </span>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-primary">{activeTab.short}</p>
                  <p className="text-lg font-extrabold text-slate-900">{activeTab.label}</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-8 sm:px-10 sm:py-10 lg:px-12 lg:py-12">
              {loading ? (
                <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
                  <LucideSymbolIcon name="progress_activity" tone="primary" className="animate-spin" size={32} />
                  <p className="text-sm font-medium text-on-surface-variant">Chargement du document…</p>
                </div>
              ) : error ? (
                <div className="flex items-start gap-3 rounded-public-card border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-800">
                  <LucideSymbolIcon name="error" tone="inherit" className="mt-0.5 shrink-0 text-red-600" size={22} />
                  <div>
                    <p className="font-bold">Chargement impossible</p>
                    <p className="mt-1">{error}</p>
                  </div>
                </div>
              ) : (
                <LegalBody raw={content} />
              )}
            </div>
          </article>
        </div>
        </PublicReveal>

        <PublicReveal animation="fade-in-up" delay={150}>
        <section className="mt-10 overflow-hidden rounded-public-card border border-outline-variant/15 bg-white shadow-lg shadow-slate-900/[0.03]">
          <div className="grid gap-6 p-6 sm:p-8 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-primary">Besoin d&apos;aide ?</p>
              <h2 className="mt-2 text-xl font-extrabold text-slate-900">Une question sur ces conditions ?</h2>
              <p className="mt-2 text-sm leading-relaxed text-on-surface-variant">
                Notre équipe peut vous expliquer les engagements liés à votre inscription ou à l&apos;usage de la plateforme.
              </p>
              <a
                href={`mailto:${supportEmail}`}
                className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
              >
                <LucideSymbolIcon name="mail" tone="inherit" size={18} />
                {supportEmail}
              </a>
            </div>
            <Link
              to="/#vacations"
              className="inline-flex items-center justify-center gap-2 rounded-public-btn bg-primary px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-primary/20 transition hover:bg-primary/90"
            >
              Contacter le campus
              <LucideSymbolIcon name="arrow_forward" tone="inherit" size={18} />
            </Link>
          </div>
        </section>
        </PublicReveal>
      </div>

      <PublicSiteFooter
        maxWidthClass="max-w-6xl"
        showHomeLink
        showLoginLink={false}
        activeLegal={type}
      />

      <ScrollToTopButton />
    </div>
  );
}
